import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive'
import { I18nextProvider } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import i18n from './i18n';
import genericStyles from './GenericComponents/GenericStyles.module.css';
import styles from './App.module.css';
import { CCProvider, useCCContext } from './CC/CCContext';
import './CC/CC';
import Startup from './Startup';
import User from './User/User';
import Mobile from './Mobile/Mobile';
import Header from './Header/Header';
import HeaderMobile from './Header/HeaderMobile';
import Footer from './Footer/Footer';
import SimpleSearchBox from './Search/SimpleSearchBox';
import Filter from './Filter/Filter';
import Facet from './Facet/Facet';
import HierarchicalFacet from './Facet/HierarchicalFacet';
import Navigator from './Navigator/Navigator';
import ViewMode from './Navigator/ViewMode';
import BrowseMode from './Navigator/BrowseMode';
import ResultSet from './Result/ResultSet';
import Map from './Record/Map';
import QuerySummary from './Result/QuerySummary';
import Sort from './Navigator/Sort';
import EmbedCount from './Embed/EmbedCount';
import EmbedFacet from './Embed/EmbedFacet';
import EmbedArticle from './Embed/EmbedArticle';
import DebugCC from './Diagnostics/DebugCC';
import RightClickPopup from './GenericComponents/RightClickPopup';
import HomepageBlock from './HomePageblock';
import GenericIconButton from './GenericComponents/GenericIconButton';
import AlbumViewer from './AlbumEditor/AlbumViewer';
import AnnotatedImageViewer from './AlbumEditor/AnnotatedImageViewer';
import { ccConfig, CC, Search } from './CC/CC';
import * as ccUtils from './CC/utils';


function MainContent() {
  const { t } = useTranslation();
  const { cc, setCc, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData, isReady } = useCCContext();
  const ctx = useCCContext();
  const themeFacetName = i18n.language === 'fr' ? 'theme' : 'themeeng';
  const [debugOpen, setDebugOpen] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [introTextEnglish, setIntroTextEnglish] = useState<string>('');
  const [introTextFrench, setIntroTextFrench] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  //const monumentName = 'Hôtel de la Marine'; // Example monument name

  //embed stuff
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const embedType = params.get('embed');
  const facetValue = params.get('facet');
  const articleId = params.get('articleId');
  const state = params.get('state');

  const isDesktopOrLaptop = useMediaQuery({
    query: '(min-width: 1224px)'
  })
  const isBigScreen = useMediaQuery({ query: '(min-width: 1824px)' })
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' })
  const isRetina = useMediaQuery({ query: '(min-resolution: 2dppx)' })
  const queryParams = new URLSearchParams(window.location.search);
  //const isTabletOrMobile = queryParams.get('tablet') === 'true' || queryParams.get('mobile') === 'true';
  const isTabletOrMobile = window.innerWidth < 1080 || queryParams.get('tablet') === 'true' || queryParams.get('mobile') === 'true';
  const [showAnnouncement, setShowAnnouncement] = useState(false);

  useEffect(() => {
    // Check if the user has already dismissed the announcement
    const dismissed = localStorage.getItem('announcementDismissed');
    if (dismissed === 'true') {
      setShowAnnouncement(false);
    } else {
      // If not dismissed, show it
      setShowAnnouncement(true);
    }
  }, []);

  const handleDismissAnnouncement = () => {
    setShowAnnouncement(false);
    localStorage.setItem('announcementDismissed', 'true');
  };

  // Simple 15-second timer to close the banner automatically.
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAnnouncement(false);
      localStorage.setItem('announcementDismissed', 'true');
    }, 15000);
    return () => clearTimeout(timer);
  }, []);

  const getMonumentParam = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('monument') || 'no monument specified';
  };

  const handleDebugOpen = () => {
    setDebugOpen(true);
  };

  const handleDebugClose = () => {
    setDebugOpen(false);
  };

  const handleAddCustomSearch = () => {
    ccUtils.addCustomSearch(ctx, '*', 'All fields', ['masque', 'voiture'], '');
    console.log('Custom search added:', cc.customSearchCriteria);
  };

  const handleCustomSearch = () => {
    ccUtils.customSearch(ctx, '/Record/Title', 'Title', 'masque', 'Searching for Masque');
    console.log('Custom search set:', cc.customSearchCriteria);
  };

  useEffect(() => {
    const checkScrollTop = () => {
      if (!showScrollToTop && window.scrollY > 1000) {
        setShowScrollToTop(true);
      } else if (showScrollToTop && window.scrollY <= 1000) {
        setShowScrollToTop(false);
      }
    };

    window.addEventListener('scroll', checkScrollTop);

    return () => window.removeEventListener('scroll', checkScrollTop);
  }, [showScrollToTop]);

  useEffect(() => {

    const getMonumentTexts = async () => {
      try {
        const departmentFacet = cc.facets['department'];
        if (departmentFacet && departmentFacet.length === 1) {
          const monumentName = departmentFacet[0];
          const data = await ccUtils.fetchMonumentHtml(monumentName);
          if (data.records && data.records.record && data.records.record.data && data.records.record.data.record) {
            const { introeng, introfra } = data.records.record.data.record;
            setIntroTextEnglish(introeng || '');
            setIntroTextFrench(introfra || '');
          } else {
            console.error('Unexpected response structure:', data);
          }
        } else {
          const query = ccUtils.generateQuery(cc);

          if (query.includes('thème: Tapisseries') || query.includes('theme: Tapisseries')) {
            setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>THE TAPESTRY COLLECTION</strong></p> <p align="LEFT" dir="LTR">Welcome to the database of the <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/tapisseries-la-collection" target="_blank">tapestries collection of the Centre des monuments nationaux</a>, one of the richest in France!<br />Discover the Apocalypse Tapestry presented at the Château d\'Angers or that of the History of Tancred and Clorinde preserved at the Château de Châteaudun and nearly a hundred other tapestries woven from the 14th to the 19th century during your navigation.<br />For each one, you will find a documentary notice and a few words about its history.</p></span>');
            setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LA COLLECTION DES TAPISSERIES</strong></p> <p align="LEFT" dir="LTR">Bienvenue sur la base de données de <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/tapisseries-la-collection" target="_blank">la collection de tapisseries du Centre des monuments nationaux</a>, une des plus riches de France !<br />Découvrez la Tenture de l’Apocalypse présentée au château d’Angers ou encore celle de l\'Histoire de Tancrède et Clorinde conservée au château de Châteaudun et près d’une centaine autres tapisseries tissées du XIVe au XIXe siècle lors de votre navigation.<br />Pour chacune, vous trouverez une notice documentaire et quelques mots sur son histoire.<br /></p></span>');
          } else if (query.includes('thème: Biens disparus') || query.includes('theme: Missing objects')) {
            setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>LOST CULTURAL PROPERTY</strong></p> <p align="LEFT" dir="LTR">Welcome to the Centre des monuments nationaux database of lost or stolen cultural property.<br />This tool allows you to search <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/les-biens-disparus-de-nos-collections" target="_blank">the lost or stolen cultural property referenced in the CMN’s “Collectio” database</a>. <br />The items of property searched form part of the public domain of furniture (Article L 2112-1 of the French General Code of the Property of Public Persons – CG3P). These items of property are therefore inalienable, imprescriptible and exempt from seizure. <br />This search tool is intended for everyone, owners and custodians, assigning authorities, actors in the art trade including collectors, agencies combating the illicit trafficking in cultural property and customs controls. <br />It should be recalled that the information given is not exhaustive and may be enriched.</p></span>');
            setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LES BIENS DISPARUS</strong></p> <p align="LEFT" dir="LTR">Bienvenue sur la base de données <a href="https://www.monuments-nationaux.fr/magazine/nos-collections/dans-les-coulisses-de-nos-collections/les-biens-disparus-de-nos-collections" target="_blank">des biens culturels disparus ou volés du Centre des monuments nationaux</a>.<br />Cet outil permet la recherche des biens culturels volés ou disparus, référencés dans la base de données « Collectio » du Centre des monuments nationaux. <br />Les biens recherchés relèvent du domaine public mobilier (article L 2112-1 du code général de la propriété des personnes publiques – CG3P). Ces biens sont à ce titre inaliénables, imprescriptibles et insaisissables. <br />Cet outil de recherche est destiné à tous, propriétaires et dépositaires, affectataires, acteurs du marché de l&apos;art y compris collectionneurs, services de répression du trafic illicite des biens culturels ou de contrôle douanier. <br /><br />Il convient de rappeler que les informations présentées ne sont pas exhaustives et pourront être enrichies.</p></span>');
          } else if (query.includes('thème: Photographies') || query.includes('theme: Photographs')) {
            setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>PHOTOGRAPHY</strong></p><p align="LEFT" dir="LTR">Welcome to the database of photographs held by the Centre des monuments nationaux.<br />This tool allows you to search for photographs cataloged in the Centre des monuments nationaux’s &quot;Collectio&quot; inventory database.</p><p align="LEFT" dir="LTR">To mark the bicentenary of photography, over 1000 records covering black-and-white and color prints, photo albums, film negatives and glass plates, postcards, and photographic equipment are available for viewing. This little-known collection highlights various holdings from sites such as George Sand’s house in Nohant; the Château de Talcy, featuring daguerreotypes by Albert Stapfer (a pioneer of the medium in 1840); and Montmajour Abbey, with its collection of contemporary photographs.</p><p align="LEFT" dir="LTR">This section will be regularly updated with new records.</p></span>');
            setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LA PHOTOGRAPHIE</strong></p><p align="LEFT" dir="LTR">Bienvenue sur la base de données des photographies conservées par le Centre des monuments nationaux.<br />Cet outil permet la recherche des photographies référencées dans la base d’inventaire « Collectio » du Centre des monuments nationaux.</p><p align="LEFT" dir="LTR">A l’occasion du bicentenaire de la Photographie, plus de 1000 notices de clichés noir et blanc ou couleur, d’albums photos, de négatifs souples ou plaques de verre, de cartes postales et de matériels photographiques sont consultables. Cette collection méconnue met en lumière plusieurs fonds provenant de monuments tels que la maison de George Sand à Nohant, le château de Talcy avec les daguerréotypes d&apos;Albert Stapfer, un des pionniers en 1840, ou encore l’abbaye de Montmajour avec une collection de photographies contemporaines.</p><p align="LEFT" dir="LTR">Ce thème sera régulièrement enrichi de nouvelles notices.</p></span>');
          } else {
            setIntroTextEnglish('<span lang="EN"><p align="LEFT" dir="LTR"><strong>THE COLLECTION OF THE CENTRE DES MONUMENTS NATIONAUX</strong></p> <p align="LEFT" dir="LTR">This database brings together the collections of a certain number of monuments from the Centre des monuments nationaux network. Others will gradually enrich this fund.</p></span>');
            setIntroTextFrench('<span lang="FR"><p align="LEFT" dir="LTR"><strong>LES COLLECTIONS DU CENTRE DES MONUMENTS NATIONAUX</strong></p> <p align="LEFT" dir="LTR">Cette base rassemble les collections d’un certain nombre de monuments du réseau du Centre des monuments nationaux. D’autres seront amenées progressivement à enrichir ce fonds.</p></span>');
          }
        }
      } catch (error) {
        console.error('Error fetching monument data:', error);
      }
    };

    const getMonumentImage = async () => {
      try {
        const departmentFacet = cc.facets['department'];
        console.log("cc", JSON.stringify(cc));
        console.log("departmentFacet", departmentFacet);

        if (departmentFacet && departmentFacet.length === 1) {
          const monumentName = departmentFacet[0];
          const imageUrl = await ccUtils.fetchMonumentImage(monumentName);
          setImageUrl(imageUrl);
          console.log("setImageUrl", imageUrl);
        } else {
          const query = ccUtils.generateQuery(cc);

          if (query.includes('thème: Tapisseries') || query.includes('theme: Tapisseries')) {
            setImageUrl('CMN_dbw16_0863.jpg');
            console.log("setImageUrl", "tapis");
          } else if (query.includes('thème: Biens disparus') || query.includes('theme: Biens disparus')) {
            setImageUrl('https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/034/images%20origine/Campagne%201983/ALC83-1420.jpg&width=600&_height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes');
            console.log("setImageUrl", "biens disparus");
          } else if (query.includes('thème: Photographies') || query.includes('theme: Photographs')) {
            setImageUrl('https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/040/Images%20pleine%20page/bgw16_0331.jpg&width=600&_height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes');
            console.log("setImageUrl", "photographies");
          } else {
            setImageUrl('121755--DBW23-0113.jpg');
            console.log("setImageUrl", "homepage");
          }
        }
      } catch (error) {
        console.error('Error fetching monument data:', error);
      }
    };

    getMonumentTexts();
    getMonumentImage();

  }, [cc])

  if (!isReady) {
    return <div></div>;
  }

  if (embedType === 'count') {
    return <EmbedCount state={state} ccContext={ctx} />;
  }

  if (embedType === 'facet' && facetValue) {
    return <EmbedFacet facetName={facetValue} />
  }

  if (embedType === 'article' && articleId) {
    return <EmbedArticle articleId={articleId} ccContext={ctx} />
  }

  return (
    <I18nextProvider i18n={i18n}>
      
      {isTabletOrMobile ? (
        <Mobile></Mobile>
      ) : (
        <div className={styles.App}>
          <Header />
          <div style={{ 'display': 'none' }}>
            <div onClick={handleAddCustomSearch} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Click me to add custom search
            </div>
            <div onClick={handleCustomSearch} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Click me to set custom search
            </div>
            <div onClick={() => ccUtils.activateFilter(ctx, "objects")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Activate Object Filter
            </div>
            <div onClick={() => ccUtils.deactivateFilter(ctx, "objects")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Deactivate Object Filter
            </div>
            <div onClick={() => ccUtils.clearFacet(ctx, "words")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Clear Words Facet
            </div>
            <div onClick={() => ccUtils.addFacetValue(ctx, "words", "bois")} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Add New Word to Words Facet
            </div>
            <div onClick={() => ccUtils.addFacetValue(ctx, "words", ["bois", "voiture"])} style={{ padding: '0px', margin: '0px', border: '1px solid black', cursor: 'pointer' }}>
              Add New Words to Words Facet
            </div>
            <div onClick={() => ccUtils.clearSearch(ctx)} style={{ padding: '20px', margin: '10px', border: '1px solid black', cursor: 'pointer' }}>
              Clear Search
            </div>
          </div>

          <div className={styles.debugLink} onClick={handleDebugOpen}>
            <RightClickPopup url="https://example.com/record">Debug</RightClickPopup></div>

          <div className={styles.container}>
            <div className={styles.middlePane}>
              <div className={styles.bottomMiddlePane}>
                <div className={styles.leftColumn}>
                  <div className={styles.filters} style={{ display: "none" }}>
                    <div className={styles.objetsIcono}>
                      <Filter filterName="filter_objects" />
                      <Filter filterName="filter_icono" />
                      <Filter filterName="filter_freeOfRights" />
                    </div>
                  </div>

                  <div className={styles.facets}>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['department'] && cc.facets['department'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_department")}</span>
                        {cc.facets['department'] && cc.facets['department'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='department' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['title'] && cc.facets['title'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_title")}</span>
                        {cc.facets['title'] && cc.facets['title'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='title' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['personnes'] && cc.facets['personnes'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_persons")}</span>
                        {cc.facets['personnes'] && cc.facets['personnes'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='personnes' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['medium'] && cc.facets['medium'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_medium")}</span>
                        {cc.facets['medium'] && cc.facets['medium'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='medium' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['datedperiod'] && cc.facets['datedperiod'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_datedperiod")}</span>
                        {cc.facets['datedperiod'] && cc.facets['datedperiod'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='datedperiod' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets['objectname'] && cc.facets['objectname'].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_objectname")}</span>
                        {cc.facets['objectname'] && cc.facets['objectname'].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet facetName='objectname' contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    <div className={styles.facetContainer}>
                      <div className={`${styles.facetName} ${cc.facets[themeFacetName] && cc.facets[themeFacetName].length > 0 ? styles.facetNameSelected : ''}`}>
                        <span>{t("facet_theme")}</span>
                        {cc.facets[themeFacetName] && cc.facets[themeFacetName].length > 0 ?
                          <span className={styles.facetActiveDot}></span> : null}
                      </div>
                      <div className={styles.facet}>
                        <Facet key={themeFacetName} facetName={themeFacetName} contentHeight='160px' sort='alpha' />
                      </div>
                    </div>
                    {(() => {
                      const themeParam = new URLSearchParams(window.location.search).get('theme');
                      const theme = themeParam ? themeParam.toLowerCase().trim() : '';
                      const query = ccUtils.generateQuery(cc).toLowerCase();

                      const isRelevantTheme = theme === 'tapisseries' ||
                                              theme === 'biens disparus' ||
                                              theme === 'photographies';
                      const isRelevantQuery = query.includes('thème: tapisseries') || query.includes('theme: tapisseries') ||
                                              query.includes('thème: biens disparus') || query.includes('theme: missing objects') ||
                                              query.includes('thème: photographies') || query.includes('theme: photographs');

                      return isRelevantTheme || isRelevantQuery;
                    })() && (
                      <div className={styles.facetContainer}>
                        <div className={`${styles.facetName} ${cc.facets['collection'] && cc.facets['collection'].length > 0 ? styles.facetNameSelected : ''}`}>
                          <span>{t("facet_collection")}</span>
                          {cc.facets['collection'] && cc.facets['collection'].length > 0 && (
                            <span className={styles.facetActiveDot}></span>
                          )}
                        </div>
                        <div className={styles.facet}>
                          <Facet facetName='collection' contentHeight='160px' sort='alpha' />
                        </div>
                      </div>
                    )}

                    {false && <>
                      <div className={styles.facetContainer}>
                        <div className={`${styles.facetName} ${cc.hierarchicalFacets['culture'] && cc.hierarchicalFacets['culture'].length > 0 ? styles.facetNameSelected : ''}`}>
                          <span>{t("facet_culture")}</span>
                          {cc.hierarchicalFacets['culture'] && cc.hierarchicalFacets['culture'].length > 0 ?
                            <span className={styles.facetActiveDot}></span> : null}
                        </div>
                        <div className={styles.facet}>
                          <HierarchicalFacet hierarchicalFacetName='culture' contentHeight='160px' sort='alpha' showSelectedAbove={false} />
                        </div>
                      </div>
                      <div className={styles.facetContainer}>
                        <div className={`${styles.facetName} ${cc.hierarchicalFacets['toponyme'] && cc.hierarchicalFacets['toponyme'].length > 0 ? styles.facetNameSelected : ''}`}>
                          <span>{t("facet_geography")}</span>
                          {cc.hierarchicalFacets['toponyme'] && cc.hierarchicalFacets['toponyme'].length > 0 ?
                            <span className={styles.facetActiveDot}></span> : null}
                        </div>
                        <div className={styles.facet}>
                          <HierarchicalFacet hierarchicalFacetName='toponyme' contentHeight='160px' sort='alpha' showSelectedAbove={false} />
                        </div>
                      </div>
                    </>
                    }
                  </div>
                </div>

                {viewMode !== 'homepage' ? (
                  <div className={styles.homePageButton}>
                    <GenericIconButton
                      icon='light/house.svg'
                      hoverEnabled={true}
                      width='28px'
                      height='28px'
                      onClick={(e: any) => {
                        e.preventDefault();
                        //const newState = { "Searches": [{ "identifier": "simpleSearch", "field": "All search fields" }], "matchAllCriteria": false };
                        //const newStateCc = ccUtils.convertStateToCC(newState);
                        //setCc(newStateCc);
                        setViewMode('homepage');
                      }}
                    />
                  </div>
                ) : (null)
                }
                <div className={styles.rightColumn}>
                  <div className={styles.searchContainer}>
                    <div className={styles.simpleSearchBox}>
                      <SimpleSearchBox Fields="simple" id="simpleSearch" showSearchButton={true} />
                    </div>
                  </div>

                  {viewMode !== 'homepage' ?
                    <div>
                      {viewMode !== "map" &&
                        <div className={styles.displayControl}>
                          <ViewMode />
                          <div className={styles.displayControlSeperator} />
                          <div className={styles.displayControlItem}>
                            <BrowseMode />
                          </div>
                          {browseMode !== 'scroll' &&
                            (<>
                              <div className={styles.displayControlSeperator} />
                              <div className={styles.displayControlItem}>
                                <Navigator />
                              </div>
                            </>
                            )
                          }
                          <Sort />
                        </div>
                      }
                      {viewMode == "map" && (<div style={{ marginTop: '20px', marginBottom: '20px' }}>
                        <ViewMode />
                        <div className={styles.hyperlinkBackToCollection} style={{ "display": "none" }} onClick={(e) => { setViewMode('list') }}>{t("map_return_to_list")}</div>
                      </div>
                      )
                      }
                      <QuerySummary />

                      {viewMode == "map" && (
                        <div className={styles.map}>
                          <Map dzi="https://collections.quaibranly.fr/pod23/cc/imageproxy.ashx?server=localhost&port=15012&filename=images/MQB_CC3V2_02_21031210_PH.dzi" />
                        </div>
                      )
                      }

                      {
                        viewMode !== "map" ? (
                          <>
                            <ResultSet />
                            {browseMode !== 'scroll' && (
                              <div className={styles.navigatorBottom}>
                                <div className={styles.displayControlSeperator} />
                                <div className={styles.displayControlItem}>
                                  <Navigator />
                                </div>
                              </div>
                            )}
                          </>
                        ) : null
                      }

                    </div>
                    :
                    <div>
                      <div className={styles.intro}>
                        <div className={styles.introText}>
                          {imageUrl && <AnnotatedImageViewer imageName={imageUrl} />}
                          {i18n.language === 'en' ? <div className={styles.introTextText} dangerouslySetInnerHTML={{ __html: introTextEnglish }} /> : i18n.language === 'fr' ? <div className={styles.introTextText} dangerouslySetInnerHTML={{ __html: introTextFrench }} /> : ''}
                        </div>
                        <div className={styles.introHyperlink} onClick={(e) => { setViewMode('list') }}>{t("homepage_click_to_view")}</div>
                      </div>
                      {false && (
                        <div>
                          <AlbumViewer height={400} albumId="56bd8fd2-2592-4a57-936e-491022607b41" />
                          <div style={{ height: '400px', overflow: 'auto' }}>
                            <EmbedArticle ccContext={ctx} articleId="160d5d4e-7c5a-4b84-b81e-47b577b58395" />
                          </div>
                        </div>
                      )}
                      <div className={styles.homepageBlocks}>
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            ccUtils.clearFacet(ctx, 'department');
                            if (i18n.language === 'fr') {
                              ccUtils.addCustomSearch(ctx, '/record/theme', 'Thème', ['[thème: Tapisseries]'], '');
                            } else {
                              ccUtils.addCustomSearch(ctx, '/record/themeeng', 'Theme', ['[theme: Tapisseries]'], '');
                            }

                            setViewMode('list');
                          }}

                          imageUrl={"https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/033/images%20pleine%20page/CMN_dbw16_0865.jpg&width=350&_height=220&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_tapisseries")}
                        />
                        <HomepageBlock
                          onClick={(e) => {
                            e.preventDefault();
                            ccUtils.clearFacet(ctx, 'department');
                            if (i18n.language === 'fr') {
                              ccUtils.addCustomSearch(ctx, '/record/theme', 'Thème', ['[thème: Biens disparus]'], '');
                            } else {
                              ccUtils.addCustomSearch(ctx, '/record/themeeng', 'Theme', ['[theme: Missing objects]'], '');
                            }

                            setViewMode('list');
                          }}

                          imageUrl={"https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/034/images%20origine/Campagne%201983/ALC83-1420_small.jpg&width=600&_height=160&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                          text={t("homepageblock_biensdisparus")}
                        />
                        <div className={styles.homepageBlockSecondRow}>
                          <HomepageBlock
                            onClick={(e) => {
                              e.preventDefault();
                              ccUtils.clearFacet(ctx, 'department');
                              if (i18n.language === 'fr') {
                                ccUtils.addCustomSearch(ctx, '/record/theme', 'Thème', ['[thème: Photographies]'], '');
                              } else {
                                ccUtils.addCustomSearch(ctx, '/record/themeeng', 'Theme', ['[theme: Photographs]'], '');
                              }

                              setViewMode('list');
                            }}

                            imageUrl={"https://collections.monuments-nationaux.fr/ccImageProxy.ashx?filename=images/040/Images%20pleine%20page/bgw16_0331.jpg&width=350&_height=220&borderwidth=0&borderheight=0&bordercolor=e8e8e8&bg=f8f8f8&passepartoutwidth=0&passepartoutheight=0&passepartoutcolor=f8f8f8&cache=yes"}
                            text={t("homepageblock_photographies")}
                          />
                        </div>
                      </div>

                    </div>
                  }
                </div>
              </div>
            </div>
          </div>
          {debugOpen && <DebugCC onClose={handleDebugClose} />}
          {
            showScrollToTop &&
            <div className={styles.scrollToTop} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <GenericIconButton icon='regular/chevron-up.svg' width='24px' height='24px' />
            </div>
          }
          <Footer />
        </div>
      )}
    </I18nextProvider>
  );
}

function App() {
  useEffect(() => {
    console.log('initialize app');
  }, []);

  return (
    <Router>
      <CCProvider>
        <MainContent />
      </CCProvider>
    </Router>
  );
}

export default App;
